import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, Lock, Phone, Mail, Check, AlertCircle } from 'lucide-react';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { trackClick, trackFunnelStep } from '@/utils/analytics';
import { getRedirectTo, logAuthRedirect } from '@/utils/authRedirect';

interface InitialFormData {
  name: string;
  age: string;
  state: string;
  coverageAmount: string;
  smokingStatus: string;
}

interface EnhancedUserData extends InitialFormData {
  policyType?: string;
  healthStatus?: string;
  healthConditions?: string;
  priority?: string;
  noMedicalExam?: string;
  approvalPreference?: string;
}

interface ModernAuthProps {
  initialData: InitialFormData;
  enhancedData?: EnhancedUserData;
  onAuthSuccess: (user: any) => void;
  mode?: 'signup' | 'signin';
  hideModeToggle?: boolean;
}

export const ModernAuth: React.FC<ModernAuthProps> = ({ 
  initialData, 
  enhancedData, 
  onAuthSuccess,
  mode = 'signup',
  hideModeToggle = false
}) => {
  const { toast } = useToast();
  const [authMode, setAuthMode] = useState<'email' | 'phone' | 'google'>('email');
  const [isSignUp, setIsSignUp] = useState(mode === 'signup');
  const [isLoading, setIsLoading] = useState(false);
  const [phoneStep, setPhoneStep] = useState<'phone' | 'otp'>('phone');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    otp: '',
    agreeToTerms: false
  });

  const cleanupAuthState = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
  };

  const saveUserProfile = async (userId: string, userData: any) => {
    const profileData = {
      name: initialData.name,
      age: parseInt(initialData.age),
      state: initialData.state,
      coverage_amount: initialData.coverageAmount,
      smoking_status: initialData.smokingStatus,
      ...(enhancedData && {
        policy_type: enhancedData.policyType,
        health_status: enhancedData.healthStatus,
        health_conditions: enhancedData.healthConditions,
        priority: enhancedData.priority,
        no_medical_exam: enhancedData.noMedicalExam,
        approval_preference: enhancedData.approvalPreference
      }),
      ...(formData.phone && { phone_number: formData.phone })
    };

    const { error } = await supabase
      .from('profiles')
      .update(profileData)
      .eq('user_id', userId);

    if (error) {
      console.error('Profile update error:', error);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreeToTerms) {
      toast({
        title: "Terms Required",
        description: "Please agree to the Terms of Service to continue.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      cleanupAuthState();
      
      if (isSignUp) {
        const redirectTo = getRedirectTo('/onboarding');
        logAuthRedirect('signup', redirectTo);
        
        const { data, error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: redirectTo,
            data: {
              name: initialData.name
            }
          }
        });

        if (error) throw error;

        if (data.user) {
          trackFunnelStep('account_created');
          await saveUserProfile(data.user.id, data.user);
          toast({
            title: "Account Created!",
            description: "Welcome to Safurra! Your quote has been saved.",
          });
          onAuthSuccess(data.user);
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (error) throw error;

        if (data.user) {
          await saveUserProfile(data.user.id, data.user);
          toast({
            title: "Welcome Back!",
            description: "Your quote has been updated with your latest preferences.",
          });
          onAuthSuccess(data.user);
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast({
        title: isSignUp ? "Signup Failed" : "Login Failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (phoneStep === 'phone') {
        const { error } = await supabase.auth.signInWithOtp({
          phone: formData.phone,
          options: {
            data: {
              name: initialData.name
            }
          }
        });

        if (error) throw error;

        toast({
          title: "OTP Sent!",
          description: "Check your phone for the verification code.",
        });
        setPhoneStep('otp');
      } else {
        const { data, error } = await supabase.auth.verifyOtp({
          phone: formData.phone,
          token: formData.otp,
          type: 'sms'
        });

        if (error) throw error;

        if (data.user) {
          await saveUserProfile(data.user.id, data.user);
          toast({
            title: "Phone Verified!",
            description: "Redirecting to setup your profile...",
          });
          // Redirect to onboarding instead of calling onAuthSuccess
          window.location.href = '/onboarding';
        }
      }
    } catch (error: any) {
      console.error('Phone auth error:', error);
      toast({
        title: "Verification Failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    
    // Track Google auth attempt
    trackClick('google_auth_attempt', 'authentication');
    
    try {
      cleanupAuthState();
      
      const redirectTo = getRedirectTo('/onboarding');
      logAuthRedirect('google-signin', redirectTo);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            data: JSON.stringify({
              name: initialData.name
            })
          }
        }
      });

      if (error) throw error;

      // Note: Google auth will redirect, so we don't handle success here
      toast({
        title: "Redirecting to Google...",
        description: "Please complete the authentication process.",
      });
    } catch (error: any) {
      console.error('Google auth error:', error);
      toast({
        title: "Google Auth Failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const isEmailFormValid = formData.email && formData.password.length >= 6 && formData.agreeToTerms;
  const isPhoneFormValid = formData.phone && isValidPhoneNumber(formData.phone) && formData.agreeToTerms;
  const isOtpFormValid = formData.otp.length === 6;

  return (
    <Card className="w-full max-w-md mx-auto shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
      <CardHeader className="text-center pb-6">
        <div className="mx-auto mb-4 p-3 rounded-full w-fit">
          <img 
            src="/lovable-uploads/0a3243f2-c9d9-43be-8f37-54dd1750981b.png" 
            alt="Safurra" 
            className="h-12 w-12"
          />
        </div>
        <CardTitle className="text-2xl font-bold text-foreground">
          {isSignUp ? 'Create Your Account' : 'Welcome Back'}
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          {isSignUp ? 'Join thousands saving on life insurance' : 'Sign in to your account'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Primary: Email & Password Form */}
        <form onSubmit={handleEmailAuth} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder={isSignUp ? "Create password (min. 6 chars)" : "Enter your password"}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              minLength={6}
              required
              className="h-11"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="email-terms"
              checked={formData.agreeToTerms}
              onCheckedChange={(checked) => setFormData({ ...formData, agreeToTerms: checked as boolean })}
            />
            <Label htmlFor="email-terms" className="text-sm text-muted-foreground">
              I agree to the Terms of Service and Privacy Policy
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full h-11 text-base font-semibold" 
            disabled={!isEmailFormValid || isLoading}
          >
            {isLoading ? 'Processing...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </Button>

          {!hideModeToggle && (
            <div className="text-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsSignUp(!isSignUp)}
              >
                {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
              </Button>
            </div>
          )}
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        {/* Alternative Sign-in Options */}
        <div className="space-y-3">
          {/* Google Sign-in */}
          <Button
            type="button"
            onClick={handleGoogleAuth}
            disabled={isLoading}
            variant="outline"
            className="w-full h-11 text-base font-medium"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? 'Connecting...' : 'Continue with Google'}
          </Button>

          {/* Phone Sign-in */}
          <Button
            type="button"
            onClick={() => setAuthMode('phone')}
            variant="outline"
            className="w-full h-11 text-base font-medium"
          >
            <Phone className="mr-2 h-4 w-4" />
            Sign in with Phone Number
          </Button>
        </div>

        {/* Phone Auth Modal/Form */}
        {authMode === 'phone' && (
          <div className="mt-6 p-4 border border-border rounded-lg bg-muted/20">
            <form onSubmit={handlePhoneAuth} className="space-y-4">
              {phoneStep === 'phone' ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                    <PhoneInput
                      id="phone"
                      value={formData.phone}
                      onChange={(value) => setFormData({ ...formData, phone: value })}
                      required
                    />
                    {formData.phone && !isValidPhoneNumber(formData.phone) && (
                      <div className="flex items-center gap-1 text-sm text-destructive">
                        <AlertCircle className="h-3 w-3" />
                        <span>Please enter a valid phone number</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="phone-terms"
                      checked={formData.agreeToTerms}
                      onCheckedChange={(checked) => setFormData({ ...formData, agreeToTerms: checked as boolean })}
                    />
                    <Label htmlFor="phone-terms" className="text-sm text-muted-foreground">
                      I agree to receive SMS verification code
                    </Label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-11" 
                    disabled={!isPhoneFormValid || isLoading}
                  >
                    {isLoading ? 'Sending Code...' : 'Send Verification Code'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAuthMode('email')}
                    className="w-full"
                  >
                    Back to email sign-in
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-sm font-medium">Verification Code</Label>
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={formData.otp}
                      onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                      maxLength={6}
                      required
                      className="h-11 text-center text-lg tracking-widest"
                    />
                    <p className="text-sm text-muted-foreground text-center">
                      Code sent to {formData.phone}
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-11" 
                    disabled={!isOtpFormValid || isLoading}
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPhoneStep('phone')}
                    className="w-full"
                  >
                    Back to phone number
                  </Button>
                </>
              )}
            </form>
          </div>
        )}

        {/* Benefits Section */}
        <div className="grid grid-cols-1 gap-2 pt-4 border-t border-border">
          <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
            <Check className="h-3 w-3 text-primary" />
            <span>100% Free & Secure</span>
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
            <Lock className="h-3 w-3 text-primary" />
            <span>Bank-level Security & Privacy</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};